/**
 * Default Node/Edge Converters
 *
 * Converts CanvasNode/CanvasEdge to renderable format for DirectCanvas.
 * Handles drag offset, selection state, z-order bonus, and rotation.
 * Apps can provide custom converters to add app-specific data.
 */

import type { ViewState, CanvasNode, CanvasEdge, Point } from "./types";
import { getFinalRotation } from "./view-state";

// =============================================================================
// Selection Z-Bonus
// =============================================================================

/** Z-order bonus for selected nodes (renders them above non-selected) */
export const SELECTION_Z_BONUS = 10000;

/** Z-order bonus for sticky notes (always render on top of regular nodes, even selected ones) */
export const STICKY_NOTE_Z_BONUS = 20000;

// =============================================================================
// Node Converter
// =============================================================================

export type NodeConverter = (
  node: CanvasNode,
  state: ViewState,
  dragOffset: Point | null,
) => CanvasNode;

export type EdgeConverter = (edge: CanvasEdge, state: ViewState) => CanvasEdge;

/**
 * Default node converter.
 * Handles drag offset, selection/hover state, z-order, and rotation.
 *
 * To get additional per-node data (type, label, custom data), provide a
 * custom `convertNode` callback in CanvasEditorOptions that wraps this
 * function or provides its own implementation.
 */
export function defaultNodeToUINode(
  node: CanvasNode,
  state: ViewState,
  dragOffset: Point | null,
): CanvasNode {
  const isSelected = state.selection.has(node.id);
  const isDragging = state.drag?.nodes.includes(node.id) ?? false;
  const isResizing = state.resize?.nodeId === node.id;
  const isRotating = state.rotate?.nodeId === node.id;
  const isHovered = state.hovered.nodeId === node.id;

  // Apply drag offset if this node is being dragged (for move, not copy)
  let position = node.position;
  if (isDragging && dragOffset && !state.drag?.copying) {
    position = {
      x: node.position.x + dragOffset.x,
      y: node.position.y + dragOffset.y,
    };
  }

  // Determine effective rotation (use in-progress rotation during rotation drag)
  let rotation = node.rotation;
  if (isRotating) {
    const inProgressRotation = getFinalRotation(state);
    if (inProgressRotation !== null) {
      rotation = inProgressRotation;
    }
  }

  return {
    id: node.id,
    type: node.type ?? "topic",
    position,
    z:
      (node.z ?? 0) +
      (node.type === "sticky-note" ? STICKY_NOTE_Z_BONUS : 0) +
      (isSelected ? SELECTION_Z_BONUS : 0),
    size: node.size,
    data: node.data ?? {},
    selected: isSelected,
    hovered: isHovered,
    dragging: isDragging,
    resizing: isResizing,
    rotating: isRotating,
    rotation,
    className: node.className,
  };
}

/**
 * Default edge converter.
 */
export function defaultEdgeToUIEdge(
  edge: CanvasEdge,
  state: ViewState,
): CanvasEdge {
  const isSelected = state.selection.has(edge.id);
  const isHovered = state.hovered.edgeId === edge.id;

  return {
    id: edge.id,
    type: edge.type ?? "default",
    source: edge.source ?? edge.sourceNodeId,
    target: edge.target ?? edge.targetNodeId,
    data: edge.data ?? {},
    selected: isSelected,
    hovered: isHovered,
  };
}
